import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/HighlightOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import apiService from '../services/api';
import type {
  CaptainApplication,
  ApproveApplicationResponse,
  UnassignedBoat,
} from '../types';

const STATUS_LABEL: Record<CaptainApplication['status'], string> = {
  PENDING: '대기',
  APPROVED: '승인',
  REJECTED: '거부',
};

const STATUS_COLOR: Record<
  CaptainApplication['status'],
  'warning' | 'success' | 'error'
> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

/**
 * 사장님 등록 신청 관리 페이지.
 * 대기/승인/거부 탭 + 상세 팝업에서 승인/거부.
 */
export default function CaptainApplications() {
  const [tab, setTab] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>(
    'PENDING',
  );
  const [items, setItems] = useState<CaptainApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<CaptainApplication | null>(null);
  const [approveResult, setApproveResult] =
    useState<ApproveApplicationResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getCaptainApplications({
        status: tab,
        pageSize: 100,
      });
      setItems(res.items);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        사장님 등록 신청
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
        >
          <Tab label="대기중" value="PENDING" />
          <Tab label="승인됨" value="APPROVED" />
          <Tab label="거부됨" value="REJECTED" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>신청일</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>배 이름</TableCell>
              <TableCell>지역</TableCell>
              <TableCell>연락처</TableCell>
              <TableCell>이메일</TableCell>
              <TableCell align="right">액션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {tab === 'PENDING'
                      ? '대기중인 신청이 없습니다'
                      : `${STATUS_LABEL[tab]}된 신청이 없습니다`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(a.createdAt).toLocaleDateString('ko-KR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.createdAt).toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>{a.applicantName}</TableCell>
                  <TableCell>
                    <strong>{a.boatName}</strong>
                  </TableCell>
                  <TableCell>{a.region}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {a.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {a.email ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelected(a)}
                    >
                      {tab === 'PENDING' ? '검토' : '상세'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 검토/승인 다이얼로그 */}
      {selected && !approveResult && (
        <ReviewDialog
          application={selected}
          onClose={() => setSelected(null)}
          onApproved={(res) => {
            setApproveResult(res);
            setSelected(null);
            load();
          }}
          onRejected={() => {
            setSelected(null);
            load();
          }}
        />
      )}

      {/* 승인 완료 후 계정 정보 표시 */}
      {approveResult && (
        <ApproveResultDialog
          result={approveResult}
          onClose={() => setApproveResult(null)}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────

function ReviewDialog({
  application,
  onClose,
  onApproved,
  onRejected,
}: {
  application: CaptainApplication;
  onClose: () => void;
  onApproved: (r: ApproveApplicationResponse) => void;
  onRejected: () => void;
}) {
  const [mode, setMode] = useState<'view' | 'approve' | 'reject'>('view');
  const [username, setUsername] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [selectedBoats, setSelectedBoats] = useState<UnassignedBoat[]>([]);
  const [boatOptions, setBoatOptions] = useState<UnassignedBoat[]>([]);
  const [boatSearch, setBoatSearch] = useState('');
  const [useNewBoat, setUseNewBoat] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPending = application.status === 'PENDING';

  useEffect(() => {
    // 신청서의 배 이름으로 초기 검색 → 매칭되는 기존 배 후보 노출
    setBoatSearch(application.boatName);
  }, [application.boatName]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const boats = await apiService.getUnassignedBoats({
          search: boatSearch.trim() || undefined,
          limit: 20,
        });
        setBoatOptions(boats);
      } catch {
        setBoatOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [boatSearch]);

  const handleApprove = async () => {
    setError(null);
    if (!username.trim() || !/^[a-zA-Z0-9_]{3,}$/.test(username.trim())) {
      setError('아이디는 영문/숫자/언더바 3자 이상');
      return;
    }
    if (selectedBoats.length === 0 && !useNewBoat) {
      setError('기존 배 선택하거나 신규 배 등록 옵션 켜기');
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.approveCaptainApplication(application.id, {
        username: username.trim(),
        boatIds: selectedBoats.length > 0 ? selectedBoats.map((b) => b.id) : undefined,
        newBoatName: useNewBoat ? application.boatName : undefined,
        newBoatRegion: useNewBoat ? application.region : undefined,
      });
      onApproved(res);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '승인 실패');
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('거부 사유를 입력하세요');
      return;
    }
    setLoading(true);
    try {
      await apiService.rejectCaptainApplication(application.id, rejectReason.trim());
      onRejected();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '거부 실패');
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        사장님 신청 #{application.id}
        <Chip
          size="small"
          label={STATUS_LABEL[application.status]}
          color={STATUS_COLOR[application.status]}
          sx={{ ml: 1 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Field label="이름" value={application.applicantName} />
          <Field label="배 이름" value={application.boatName} />
          <Field label="지역" value={application.region} />
          <Field label="연락처" value={application.phone} monospace />
          <Field label="이메일" value={application.email ?? '-'} />
          <Field label="메모" value={application.note ?? '-'} multiline />
          <Field
            label="신청일"
            value={new Date(application.createdAt).toLocaleString('ko-KR')}
          />
          {application.businessRegDocUrl ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                사업자등록증
              </Typography>
              <a
                href={application.businessRegDocUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={application.businessRegDocUrl}
                  alt="사업자등록증"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    display: 'block',
                  }}
                />
              </a>
              <Typography variant="caption" color="text.secondary">
                클릭하면 원본 크기로 열림
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              사업자등록증 미첨부 — 승인 신중
            </Alert>
          )}
          {application.status === 'REJECTED' && (
            <Field label="거부 사유" value={application.rejectReason ?? ''} />
          )}
        </Stack>

        {isPending && mode === 'approve' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              계정 생성 정보
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="아이디 (관리자 지정)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                size="small"
                helperText="영문·숫자·언더바 3자 이상. 예: kimboat"
              />
              <Box>
                <Typography variant="body2" gutterBottom>
                  배 연결
                </Typography>
                <Stack spacing={1}>
                  <Button
                    size="small"
                    variant={useNewBoat ? 'contained' : 'outlined'}
                    onClick={() => setUseNewBoat(!useNewBoat)}
                  >
                    {useNewBoat ? '✓ 신청서의 배 이름으로 신규 생성' : '□ 신규 배로 생성'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    신청서 배 이름: <strong>{application.boatName}</strong> ({application.region})
                  </Typography>
                </Stack>
              </Box>
              <Autocomplete
                multiple
                options={boatOptions}
                value={selectedBoats}
                onChange={(_, val) => setSelectedBoats(val)}
                getOptionLabel={(o) => `${o.name} (${o.homePort ?? o.region ?? '지역 미상'})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onInputChange={(_, val) => setBoatSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="기존 배 매핑 (선택)"
                    size="small"
                    helperText="이미 크롤링된 배가 있으면 검색해서 매핑"
                  />
                )}
              />
            </Stack>
          </>
        )}

        {isPending && mode === 'reject' && (
          <>
            <Divider sx={{ my: 2 }} />
            <TextField
              label="거부 사유"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              helperText="신청자에게 표시되지 않지만 관리 기록으로 남음"
            />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {isPending && mode === 'view' && (
          <>
            <Button onClick={onClose}>닫기</Button>
            <Button
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setMode('reject')}
            >
              거부
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => setMode('approve')}
            >
              승인 진행
            </Button>
          </>
        )}
        {isPending && mode === 'approve' && (
          <>
            <Button onClick={() => setMode('view')}>뒤로</Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
              onClick={handleApprove}
              disabled={loading}
            >
              계정 생성 & 승인
            </Button>
          </>
        )}
        {isPending && mode === 'reject' && (
          <>
            <Button onClick={() => setMode('view')}>뒤로</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={loading}
            >
              거부 확정
            </Button>
          </>
        )}
        {!isPending && <Button onClick={onClose}>닫기</Button>}
      </DialogActions>
    </Dialog>
  );
}

function ApproveResultDialog({
  result,
  onClose,
}: {
  result: ApproveApplicationResponse;
  onClose: () => void;
}) {
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사됨');
  };
  const smsTemplate = `[어랑 사장님 계정 안내]\n${result.name}님 안녕하세요.\n사장님 등록이 완료됐습니다.\n\n앱 다운로드 후 "사장님 로그인" 화면에서\n아이디: ${result.username}\n비밀번호: ${result.initialPassword}\n\n첫 로그인 후 비밀번호를 변경해주세요.`;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>✅ 승인 완료</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>초기 비밀번호는 이 화면에서만 확인 가능합니다.</strong>
          <br />
          아래 SMS 템플릿을 복사해서 <strong>{result.phone}</strong> 로 발송해주세요.
        </Alert>

        <Stack spacing={1.5}>
          <Field label="이름" value={result.name} />
          <Field label="전화번호" value={result.phone} monospace />
          <Field label="아이디" value={result.username} monospace />
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              초기 비밀번호
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h5"
                fontFamily="monospace"
                sx={{ flex: 1, letterSpacing: 2 }}
              >
                {result.initialPassword}
              </Typography>
              <IconButton
                onClick={() => copy(result.initialPassword)}
                color="primary"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" gutterBottom>
          SMS 전달용 텍스트
        </Typography>
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            mb: 1,
          }}
        >
          {smsTemplate}
        </Box>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => copy(smsTemplate)}
        >
          SMS 전문 복사
        </Button>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({
  label,
  value,
  monospace,
  multiline,
}: {
  label: string;
  value: string;
  monospace?: boolean;
  multiline?: boolean;
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontFamily={monospace ? 'monospace' : undefined}
        sx={{ whiteSpace: multiline ? 'pre-wrap' : undefined }}
      >
        {value}
      </Typography>
    </Box>
  );
}
